import React, { useState, useEffect, FC, ReactElement, ReactNode } from "react";
import { MenuItem, Select, FormControl, InputLabel, FormHelperText, SelectChangeEvent, Skeleton, Grid } from "@mui/material";
import { useSettingsContext } from "../../../Settings/hooks/useSettingsContext";
import { useTranslation } from "react-i18next";
import { CategoryItem, CategorySelectFieldParams } from "../types/CRMCategorySelectField";
import { default as categorySelectService } from "../services/categorySelect";

const getSelectableItems = (items: CategoryItem[]) => {
  const subcategories = items.filter((item) => item.parent_id);
  return subcategories.length ? subcategories : items;
};

const CategorySelectField: FC<CategorySelectFieldParams> = ({
  onChange,
  value,
  categoryCode,
  label,
  readOnly,
  onAvailableItemsUpdate,
  onSelectUpdate,
  meta,
  autolectures,
  unsigned=false,
}) => {

  const Settings = useSettingsContext();
  const { t } = useTranslation();

  const [ state, setState ] = useState<"fetching" | "success" | "error">("fetching");
  const [availableItems, setAvailableItems] = useState<CategoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const categories: CategoryItem[] = [];
  for (const item of getSelectableItems(availableItems)) {
    const category = item.parent_id
      ? { id: item.parent_id[0], name: item.parent_id[1] }
      : item;
    if (!categories.some((existing) => existing.id === category.id)) {
      categories.push(category);
    }
  }
  const subcategories = availableItems.filter(
    (item) => item.parent_id && item.parent_id[0] === selectedCategory
  );

  const selectItem = (item: CategoryItem | null) => {
    const id = item?.id ?? null;
    setSelectedItem(id);
    setSelectedCategory(item?.parent_id ? item.parent_id[0] : id);
    onChange(id);
    if (item && typeof onSelectUpdate === "function") {
      onSelectUpdate(item.id, item.name);
    }
  };

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const id = Number(event.target.value);
    const hasSubcategories = availableItems.some(
      (item) => item.parent_id && item.parent_id[0] === id
    );
    if (hasSubcategories) {
      setSelectedCategory(id);
      setSelectedItem(null);
      onChange(null);
    } else {
      const item = availableItems.find((item) => item.id === id);
      selectItem(item ?? null);
    }
  };

  const setAutolectures = () => {
    let autoreads = Settings?.features?.contactAutoReads ?? false;
    if (autoreads){
      if (autolectures) {
        const categ = availableItems.find((ct) => ct.id === 20); //20 is the code for autolectures @lumina
        if (categ) {
          selectItem(categ);
        }
      }
    }
  }

  useEffect(() => {
    setState("fetching");

    categorySelectService.getCategories(unsigned)
    .then(response => {
      const items = response.data?.items
      if (!items) return;

      setState("success");

      if (items && typeof onAvailableItemsUpdate === "function") {
        onAvailableItemsUpdate();
      }
      
      const defaultItem = getSelectableItems(items).find((item) => item.id === value);
      setAvailableItems(items);
      selectItem(defaultItem ?? null);
      if (!defaultItem && categoryCode) {
        const category = items.find((item) => item.categ_code === categoryCode);
        setSelectedCategory(category?.id ?? null);
      }
    })
  }, []);

  useEffect(() => {
    setAutolectures();
    // eslint-disable-next-line
  }, [autolectures, availableItems]);

  return (
    <div>
      {state === "fetching" ? (
        <Skeleton data-testid="skeleton-categories" height={56} />
      ) : (
        <>
          <Grid container>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel shrink htmlFor="" id="category-name-label">
                  {(label || t('common:text.contact_category')) + "*"}
                </InputLabel>
                <Select<number>
                  value={selectedCategory ?? ''}
                  onChange={handleChange}
                  autoWidth={false}
                  labelId="category-name-label"
                  id="category-name"
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {subcategories.length > 0 && (
              <Grid item xs={12} md={6} sx={{ paddingLeft: { xs: 0, md: 2 }, marginY: { xs: 2, md: 0 } }} >
                <FormControl fullWidth disabled={readOnly}>
                  <InputLabel shrink id="subcategory-name-label">
                    {t('common:text.contact_subcategory') + "*"}
                  </InputLabel>
                  <Select<number>
                    value={selectedItem ?? ''}
                    onChange={(event) => {
                      const item = subcategories.find((item) => item.id === Number(event.target.value));
                      selectItem(item ?? null);
                    }}
                    labelId="subcategory-name-label"
                    id="subcategory-name"
                  >
                    {subcategories.map((item) => (
                      <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          {!!(meta?.touched) &&
            <FormHelperText error={true}>
              {meta?.error}
            </FormHelperText>
          }
        </>
      )}
    </div>
  );
};

export default CategorySelectField;
